/**
 * 🔒 usePageExecutionContext — Hook de gestion du contexte d'exécution par page
 * =============================================================================
 * 
 * Décision MOA (Janvier 2025):
 * - 1 page = 1 contexte d'exécution = 1 UUID
 * - Multi-onglets: chaque onglet = contexte distinct
 * - Invalidation à la sortie de page = annulation backend
 * 
 * USAGE (1 ligne par page):
 * ```javascript
 * usePageExecutionContext('coach-vocal');
 * ```
 * 
 * RESPONSABILITÉS:
 * - Générer UUID unique au montage
 * - Stocker dans window.__currentExecutionContextId (pour intercepteurs)
 * - Patcher window.fetch pour injection automatique du header
 * - Appeler /api/execution/register au montage (SYNCHRONE avant autres appels)
 * - Appeler /api/execution/invalidate au démontage
 * - Écouter beforeunload pour notification synchrone
 * 
 * 🔒 P0 FIX (Janvier 2025): Patch global de fetch pour couvrir 100% des appels
 */

import { useEffect, useRef, useLayoutEffect } from 'react';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();

// 🔒 Sauvegarde du fetch natif (une seule fois au chargement du module)
const _originalFetch = window.__nativeFetch__ || window.fetch.bind(window);

/**
 * Génère un UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * 🔒 Patch global de window.fetch pour injection automatique du header
 * Couvre 100% des appels fetch, y compris ceux dans CoachVocal.js
 */
function patchGlobalFetch() {
  if (window.__fetchPatchedForExecutionContext) {
    return; // Déjà patché
  }
  
  window.fetch = function(url, options = {}) {
    const headers = new Headers(options.headers || {});
    
    // 🔒 Injection automatique si contexte actif
    if (window.__currentExecutionContextId && !headers.has('X-Execution-Context-Id')) {
      headers.set('X-Execution-Context-Id', window.__currentExecutionContextId);
    }
    
    return _originalFetch(url, {
      ...options,
      headers
    });
  };
  
  window.__fetchPatchedForExecutionContext = true;
  console.log('[ExecutionContext] 🔧 Global fetch patched for automatic header injection');
}

/**
 * 🔒 Restaure fetch original au démontage (si plus aucun contexte actif)
 */
function unpatchGlobalFetch() {
  // Ne pas restaurer - laisser le patch actif pour toute l'app
  // Car d'autres pages peuvent aussi avoir besoin du header
}

/**
 * Hook principal de gestion du contexte d'exécution
 * 
 * @param {string} pageName - Nom de la page (ex: 'coach-vocal', 'entrainement-ecrit')
 * @returns {string} contextId - UUID du contexte actuel
 */
export function usePageExecutionContext(pageName) {
  const contextIdRef = useRef(null);
  const registeredRef = useRef(false);
  
  // 🔒 useLayoutEffect pour garantir l'exécution AVANT les premiers renders enfants
  useLayoutEffect(() => {
    // 1. Patcher fetch globalement (une seule fois)
    patchGlobalFetch();
    
    // 2. Générer UUID unique pour cette instance de page
    const contextId = generateUUID();
    contextIdRef.current = contextId;
    
    // 3. 🔒 Stocker dans window IMMÉDIATEMENT pour les intercepteurs globaux
    window.__currentExecutionContextId = contextId;
    
    console.log(`[ExecutionContext] 🚀 Created context: ${contextId} (page: ${pageName})`);
    
    // 4. Enregistrer le contexte côté backend (async mais nécessaire)
    const registerContext = async () => {
      try {
        // Utiliser _originalFetch pour éviter la récursion
        const response = await _originalFetch(`${BACKEND_URL}/api/execution/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            context_id: contextId,
            page: pageName
          }),
        });
        
        if (response.ok) {
          registeredRef.current = true;
          window.__executionContextRegistered = true;
          console.log(`[ExecutionContext] ✅ Registered: ${contextId}`);
        } else {
          console.error(`[ExecutionContext] ❌ Failed to register: ${response.status}`);
        }
      } catch (error) {
        console.error(`[ExecutionContext] ❌ Registration error:`, error);
      }
    };
    
    // 🔒 Lancer register immédiatement
    registerContext();
    
    // 5. Handler pour beforeunload (best-effort synchrone)
    const handleBeforeUnload = () => {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${BACKEND_URL}/api/execution/invalidate/${contextId}`,
          ''
        );
        console.log(`[ExecutionContext] 📡 Beacon sent for: ${contextId}`);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // 6. Cleanup au démontage
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // 🔒 Nettoyer le contexte global
      if (window.__currentExecutionContextId === contextId) {
        window.__currentExecutionContextId = null;
        window.__executionContextRegistered = false;
      }
      
      // Invalider le contexte côté backend
      const invalidateContext = async () => {
        try {
          console.log(`[ExecutionContext] ⏹️ Invalidating: ${contextId}`);
          
          // Utiliser _originalFetch avec keepalive
          await _originalFetch(`${BACKEND_URL}/api/execution/invalidate/${contextId}`, {
            method: 'POST',
            keepalive: true,
          });
          
          console.log(`[ExecutionContext] ✅ Invalidated: ${contextId}`);
        } catch (error) {
          console.log(`[ExecutionContext] ⚠️ Invalidation sent (may not complete): ${contextId}`);
        }
      };
      
      invalidateContext();
      
      // Ne pas unpatch - garder pour les autres pages
      // unpatchGlobalFetch();
    };
  }, [pageName]);
  
  return contextIdRef.current;
}

export default usePageExecutionContext;
